import { cn } from './cn';
import { Button } from './Button';

/**
 * Offset pagination for search results, which is the one public surface that has pages
 * rather than a cursor (§2: relevance ranking has no stable keyset to page by). Feeds use
 * `LoadMore` below instead, which matches the cursor the API actually returns.
 */
export function Pagination({
  page,
  pageCount,
  onChange,
  labels,
  className,
}: {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
  labels: { previous: string; next: string; status: string };
  className?: string;
}) {
  if (pageCount <= 1) return null;

  return (
    <nav
      className={cn('flex items-center justify-center gap-4', className)}
      aria-label={labels.status}
    >
      <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        {labels.previous}
      </Button>
      <span className="text-sm font-bold text-ink" aria-current="page">
        {page} / {pageCount}
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
      >
        {labels.next}
      </Button>
    </nav>
  );
}

/** The cursor equivalent: one button, disabled when the API returned no `nextCursor`. */
export function LoadMore({
  hasMore,
  loading,
  onLoad,
  label,
  className,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoad: () => void;
  label: string;
  className?: string;
}) {
  if (!hasMore) return null;
  return (
    <div className={cn('flex justify-center', className)}>
      <Button variant="secondary" size="md" loading={loading} onClick={onLoad}>
        {label}
      </Button>
    </div>
  );
}
